import InputField from "@/components/InputField";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { useExtendOfferMutation } from "@/hooks/api/offer.queries";
import { formatDate } from "@/utils/formatDate.util";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

interface OfferDialogProps {
  applicationId: string;
  internshipId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  studentName?: string;
  onSuccess?: () => void;
}

/**
 * Every field is optional on the API, but an offer with no stipend and no
 * start date is not useful to a student, so both are required here.
 */
const formSchema = z
  .object({
    stipend: z.string().min(1, "Stipend is required"),
    start_date: z.string().min(1, "Start date is required"),
    end_date: z.string().optional(),
    offer_letter_url: z
      .string()
      .url("Enter a valid URL")
      .optional()
      .or(z.literal("")),
    expires_at: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.end_date && data.end_date < data.start_date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "End date cannot be before the start date",
        path: ["end_date"],
      });
    }
    if (Number(data.stipend) < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Stipend cannot be negative",
        path: ["stipend"],
      });
    }
  });

type formType = z.infer<typeof formSchema>;

const emptyValues: formType = {
  stipend: "",
  start_date: "",
  end_date: "",
  offer_letter_url: "",
  expires_at: "",
};

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex justify-between gap-4 border-b border-gray-50 py-2 last:border-0">
    <span className="text-sm text-gray-500">{label}</span>
    <span className="text-right text-sm font-semibold text-gray-900">
      {value}
    </span>
  </div>
);

const OfferDialog = ({
  applicationId,
  internshipId,
  isOpen,
  onOpenChange,
  studentName,
  onSuccess,
}: OfferDialogProps) => {
  const mutation = useExtendOfferMutation(internshipId);

  /**
   * An application can hold only ONE offer. A second extend is silently
   * ignored server-side — the student keeps seeing the first offer and no
   * error comes back — so a wrong stipend cannot be corrected by re-sending.
   * This review step is the only guard against that.
   */
  const [pendingValues, setPendingValues] = useState<formType | null>(null);

  const form = useForm<formType>({
    resolver: zodResolver(formSchema),
    defaultValues: emptyValues,
  });

  useEffect(() => {
    if (isOpen) {
      form.reset(emptyValues);
      setPendingValues(null);
    }
  }, [isOpen, form]);

  const handleConfirm = () => {
    if (!pendingValues) return;

    mutation.mutate(
      {
        applicationId,
        data: {
          stipend: Number(pendingValues.stipend),
          start_date: pendingValues.start_date,
          end_date: pendingValues.end_date || null,
          offer_letter_url: pendingValues.offer_letter_url?.trim() || null,
          // `datetime-local` is local time; the API wants ISO-8601.
          expires_at: pendingValues.expires_at
            ? new Date(pendingValues.expires_at).toISOString()
            : null,
        },
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          onSuccess?.();
        },
      }
    );
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      form.reset(emptyValues);
      setPendingValues(null);
    }
    onOpenChange(open);
  };

  const isReviewing = !!pendingValues;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {isReviewing ? "Confirm Offer" : "Extend Offer"}
          </DialogTitle>
          <DialogDescription>
            {isReviewing
              ? "This is your last chance to change anything — an offer is final once extended."
              : `${
                  studentName ? `${studentName} receives` : "The student receives"
                } an email and sees this in their offers list. The application moves to Offer Extended.`}
          </DialogDescription>
        </DialogHeader>

        {isReviewing ? (
          <div className="space-y-4">
            <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
              <AlertTriangle
                size={18}
                className="mt-0.5 shrink-0 text-amber-600"
              />
              <p className="text-xs leading-relaxed text-amber-800">
                <strong>Offers cannot be edited, revoked or re-sent.</strong> An
                application can hold only one offer for its lifetime, so a wrong
                stipend or date cannot be corrected afterwards. Check the
                stipend and dates carefully before confirming.
              </p>
            </div>

            <div className="rounded-xl border border-gray-100 px-4 py-1">
              <SummaryRow
                label="Candidate"
                value={studentName || "This application"}
              />
              <SummaryRow
                label="Stipend"
                value={`₹${Number(pendingValues.stipend).toLocaleString("en-IN")} / month`}
              />
              <SummaryRow
                label="Start Date"
                value={formatDate(pendingValues.start_date, {
                  dateStyle: "long",
                })}
              />
              <SummaryRow
                label="End Date"
                value={
                  pendingValues.end_date
                    ? formatDate(pendingValues.end_date, { dateStyle: "long" })
                    : "Not set"
                }
              />
              <SummaryRow
                label="Offer Expires"
                value={
                  pendingValues.expires_at
                    ? formatDate(pendingValues.expires_at, {
                        dateStyle: "long",
                        timeStyle: "short",
                      })
                    : "Never"
                }
              />
              <SummaryRow
                label="Offer Letter"
                value={pendingValues.offer_letter_url?.trim() ? "Attached" : "Not attached"}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setPendingValues(null)}
                disabled={mutation.isPending}
              >
                Back to Edit
              </Button>
              <Button
                type="button"
                onClick={handleConfirm}
                disabled={mutation.isPending}
              >
                {mutation.isPending ? "Extending..." : "Confirm & Extend Offer"}
              </Button>
            </div>
          </div>
        ) : (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit((data) => setPendingValues(data))}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <InputField
                  name="stipend"
                  label="Stipend (₹ per month)"
                  type="number"
                  min="0"
                  placeholder="25000"
                  required
                />
                <InputField
                  name="expires_at"
                  label="Offer Expires At"
                  type="datetime-local"
                  description="Left blank, the offer never auto-expires."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <InputField
                  name="start_date"
                  label="Start Date"
                  type="date"
                  required
                />
                <InputField name="end_date" label="End Date" type="date" />
              </div>

              <InputField
                name="offer_letter_url"
                label="Offer Letter URL"
                type="url"
                placeholder="https://..."
              />

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">Review Offer</Button>
              </div>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OfferDialog;
